import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';

import { LoadingContext, ModeReglementContext } from '@/contexts';
import type { ModeReglementContextType } from '@/contexts/ModeReglementContext';
import { getModesReglements as getMR, getRegieModesReglement as getRMR } from '@/routes';
import type { ApiResponseType, ModeReglementType, RegieModeReglementType } from '@/types';
import { useRegieStore } from '@/zustand';

type ModeReglementContextProviderProps = {
  children: React.ReactNode;
  /** Charge les données dès que le provider est mount si true. */
  autoload?: boolean | 'régie';
};

const ModeReglementContextProvider = (props: ModeReglementContextProviderProps) => {
  const { children, autoload } = props;

  const [modesReglements, setModesReglements] = React.useState<ModeReglementType[]>();
  const [regieModesReglement, setRegieModesReglement] = React.useState<RegieModeReglementType[]>();

  const { setModesReglementsLoadings } = React.useContext(LoadingContext);
  const regie = useRegieStore((state) => state.regie);

  /** Lance la récupération des modes de règlements */
  const getModesReglements = React.useCallback(() => {
    const pendingPromises = [];
    setModesReglementsLoadings?.((prev) => ({ ...prev, modesReglements: true }));

    request
      .get(getMR())
      .then(({ data, meta: { lastPage } }: ApiResponseType<ModeReglementType>) => {
        for (let i = 2; i <= lastPage; i += 1) {
          pendingPromises.push(request.get(getMR()));
        }

        Promise.all(pendingPromises)
          .then((datas) => {
            setModesReglements([...data, ..._.flatten(datas)]);
          })
          .finally(() => {
            setModesReglementsLoadings?.((prev) => ({ ...prev, modesReglements: false }));
          });
      });
  }, [setModesReglementsLoadings]);

  /** Lance la récupération des modes de règlements de la régie sélectionnée */
  const getRegieModesReglement = React.useCallback(async () => {
    if (regie) {
      setModesReglementsLoadings?.((prev) => ({ ...prev, regieModesReglement: true }));

      return request
        .get(getRMR({ identifiantRegie: regie.identifiant }))
        .then(({ data }: { data: RegieModeReglementType[] }) => {
          setRegieModesReglement(data);
        })
        .finally(() => {
          setModesReglementsLoadings?.((prev) => ({
            ...prev,
            regieModesReglement: false,
          }));
        });
    }

    return Promise.resolve();
  }, [regie, setModesReglementsLoadings]);

  React.useEffect(() => {
    if (!_.isNil(autoload) && autoload !== false) {
      getModesReglements();

      if (autoload === 'régie') {
        getRegieModesReglement();
      }
    }
  }, [autoload, getModesReglements, getRegieModesReglement]);

  const modesReglementsActifs = React.useMemo(
    () => _.filter(modesReglements, { actif: true }),
    [modesReglements],
  );

  const context = React.useMemo<ModeReglementContextType>(
    () => ({
      modesReglements,
      modesReglementsActifs,
      regieModesReglement,
      getModesReglements,
      getRegieModesReglement,
    }),
    [
      getModesReglements,
      getRegieModesReglement,
      modesReglements,
      modesReglementsActifs,
      regieModesReglement,
    ],
  );

  return <ModeReglementContext.Provider value={context}>{children}</ModeReglementContext.Provider>;
};

export default ModeReglementContextProvider;
